// src/api/billingService.js
// Billing service for subscription management with the new /api/billing/* endpoints

import { apiFetch } from "./http";
import { API_ENDPOINTS } from "../config";

export const billingService = {
  /**
   * Get current subscription status
   * @returns {Promise<Object>} Subscription status
   * {
   *   has_subscription: boolean,
   *   subscription_status: "active" | "past_due" | "canceled" | null,
   *   subscription_plan: "starter" | "team" | "growth" | null,
   *   trial_ends_at: string | null, // ISO date
   *   trial_active: boolean,
   *   access_granted: boolean
   * }
   */
  async getStatus() {
    const response = await apiFetch(API_ENDPOINTS.BILLING_STATUS);

    if (!response.ok) {
      throw new Error("Failed to get subscription status");
    }

    return response.json();
  },

  /**
   * Get available plans
   * @returns {Promise<Object>} Available plans
   * {
   *   plans: Array<{
   *     name: string,
   *     display_name: string,
   *     seat_limit: number,
   *     description: string
   *   }>
   * }
   */
  async getPlans() {
    const response = await apiFetch(API_ENDPOINTS.BILLING_PLANS);

    if (!response.ok) {
      throw new Error("Failed to get plans");
    }

    return response.json();
  },

  /**
   * Get seat information for the current organization
   * @returns {Promise<Object>} Seat information
   * {
   *   organization_id: number,
   *   organization_name: string,
   *   subscription_plan: string | null,
   *   seat_limit: number,
   *   seats_used: number,
   *   seats_available: number,
   *   can_add_members: boolean
   * }
   */
  async getSeats() {
    const response = await apiFetch(API_ENDPOINTS.BILLING_SEATS);

    if (!response.ok) {
      throw new Error("Failed to get seat information");
    }

    return response.json();
  },

  /**
   * Create a Stripe checkout session for a specific plan
   * @param {string} plan - "starter" | "team" | "growth"
   * @returns {Promise<Object>} { url: string } - Stripe checkout URL
   */
  async createCheckout(plan) {
    const response = await apiFetch(API_ENDPOINTS.BILLING_CHECKOUT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to create checkout session");
    }

    return response.json();
  },

  /**
   * Verify a Stripe checkout session and activate the subscription server-side.
   * Called from SuccessPage using the session_id from the URL.
   * @param {string} sessionId - Stripe checkout session ID (cs_xxx)
   * @returns {Promise<Object>} Subscription status (same as getStatus)
   */
  async verifyCheckoutSession(sessionId) {
    const response = await apiFetch(API_ENDPOINTS.BILLING_VERIFY_SESSION, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to verify checkout session");
    }

    return response.json();
  },

  /**
   * Get detailed subscription info from Stripe (renewal date, price, invoices)
   * @returns {Promise<Object>} Subscription details with Stripe data
   */
  async getSubscriptionDetails() {
    const response = await apiFetch(API_ENDPOINTS.BILLING_DETAILS);

    if (!response.ok) {
      throw new Error("Failed to get subscription details");
    }

    return response.json();
  },

  /**
   * Open Stripe billing portal for subscription management
   * @returns {Promise<Object>} { url: string } - Stripe portal URL
   */
  async openPortal() {
    const response = await apiFetch(API_ENDPOINTS.BILLING_PORTAL, {
      method: "POST",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to open billing portal");
    }

    return response.json();
  },
};
